import Button from "@/components/common/Button";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import ThemeToggle from "@/components/common/ThemeToggle";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { API_BASE_URL } from "@/lib/axios";
import axios from "axios";
import { motion } from "framer-motion";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useOtpVerification } from "@/hooks/useOtpVerification";
import { setAuthToken, setLoginType, clearPendingVerification } from "@/utils/secureStorage";

const OtpVerification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateAuthState } = useAuth();
  const { theme } = useTheme();

  // Use custom hook for state management
  const {
    otp,
    setOtp,
    isLoading,
    setIsLoading,
    isResending,
    setIsResending,
    resendCountdown,
    setResendCountdown,
    isInitialized,
    email,
    userId,
    loginType,
  } = useOtpVerification();

  console.log("OTP Verification State:", {
    email,
    userId,
    loginType,
    // Data is now handled by the hook
  });

  useEffect(() => {
    if (!email) {
      console.log("Missing email, redirecting to login");
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value.split("").map((_, index) => value[index]));
    setError(null);
  };

  const verifyOtp = async () => {
    if (!email) {
      toast.error(t("auth.invalidEmail"));
      return;
    }

    if (otp.some((digit) => digit === "")) {
      toast.error(t("auth.invalidOtp"));
      return;
    }

    try {
      setIsLoading(true);

      // dynamically change
      const userType = loginType === "company" ? "company" : "user";

      console.log("Verifying OTP:", {
        email,
        userId,
        otp: otp.join(""),
        userType,
        loginType,
      });

      const response = await fetch(
        `${API_BASE_URL}/api/v1/authentication/${userType}/verify-otp?id=${userId}&otp=${otp.join(
          ""
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      const data = await response.json();
      console.log(
        "Complete verification response:",
        JSON.stringify(data, null, 2)
      );

      if (response.ok && data.status === "success") {
        if (data.data?.document) {
          const userData = data.data.document;
          console.log("Updating auth state with user data:", userData);

          // Store the token in secure storage
          if (data.token) {
            setAuthToken(data.token);
            console.log("Token stored in secure storage:", data.token);

            // Set the token in axios headers for subsequent API calls
            axios.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${data.token}`;
          }

          // Store login type before updating auth state
          if (loginType === "company") {
            setLoginType("company");
          }

          updateAuthState(userData);
          clearPendingVerification();

          // If it's a company login, always redirect to company admin dashboard
          if (loginType === "company") {
            console.log(
              "Company login detected, redirecting to company admin dashboard"
            );
            navigate("/admin/company", {
              replace: true,
              state: {
                message: t("auth.verificationSuccess"),
                type: "success",
              },
            });
            return;
          }

          // For other login types, determine redirect based on role
          let redirectPath = "/dashboard";
          if (userData.role === "department_admin") {
            redirectPath = "/department-admin/dashboard";
          } else if (userData.role === "employee") {
            redirectPath = "/employee/dashboard";
          }

          console.log("Redirecting to:", redirectPath);
          navigate(redirectPath, {
            replace: true,
            state: {
              message: t("auth.verificationSuccess"),
              type: "success",
            },
          });
        } else {
          console.error("No user data in response:", data);
          toast.error(t("auth.verificationFailed"));
        }
      } else {
        console.error("Verification failed:", data);
        toast.error(data.message || t("auth.verificationFailed"));
      }
    } catch (err: any) {
      console.error("Verification error:", err);
      const backendMsg = err?.response?.data?.message || err?.message;
      const statusCode = err?.response?.status;
      if (backendMsg) {
        if (backendMsg.includes("Invalid OTP")) {
          toast.error(t("auth.invalidOtp") || backendMsg);
        } else if (/^\d{3}$/.test(backendMsg.trim())) {
          toast.error(t("auth.verificationFailed"));
        } else {
          toast.error(backendMsg);
        }
      } else if (statusCode && (statusCode === 400 || statusCode === 500)) {
        toast.error(t("auth.verificationFailed"));
      } else {
        toast.error(t("auth.verificationFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendCountdown > 0 || !email) return;

    try {
      setIsResending(true);

      const response = await fetch(
        `${API_BASE_URL}/api/v1/authentication/user/resend-otp?email=${encodeURIComponent(
          email
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok && data.status === "success") {
        setResendCountdown(60);
      } else {
        toast.error(data.message || t("auth.resendOtpFailed"));
      }
    } catch (err: any) {
      toast.error(err.message || t("auth.resendOtpFailed"));
    } finally {
      setIsResending(false);
    }
  };

  // Show loading state during initialization
  if (!isInitialized) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
        <header className="flex h-16 items-center justify-between px-6 md:px-8">
          <div className="flex items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex items-center"
            >
              <img src="/logo.svg" alt={t("app.name")} className="h-8 w-auto" />
              <span className="ml-2 text-xl font-bold text-primary-600 dark:text-primary-400">
                {t("app.name")}
              </span>
            </motion.div>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="h-16 w-16 animate-pulse-slow rounded-full bg-primary-500"></div>
            <h1 className="text-2xl font-bold text-primary-800 dark:text-primary-200">
              Loading...
            </h1>
          </div>
        </main>
      </div>
    );
  }

  // If no email, show loading state
  if (!email) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
        <header className="flex h-16 items-center justify-between px-6 md:px-8">
          <div className="flex items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex items-center"
            >
              <img src="/logo.svg" alt={t("app.name")} className="h-8 w-auto" />
              <span className="ml-2 text-xl font-bold text-primary-600 dark:text-primary-400">
                {t("app.name")}
              </span>
            </motion.div>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="h-16 w-16 animate-pulse-slow rounded-full bg-primary-500"></div>
            <h1 className="text-2xl font-bold text-primary-800 dark:text-primary-200">
              Loading...
            </h1>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
      <header className="flex h-16 items-center justify-between px-6 md:px-8">
        <div className="flex items-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <img src="/logo.svg" alt={t("app.name")} className="h-8 w-auto" />
            <span className="ml-2 text-xl font-bold text-primary-600 dark:text-primary-400">
              {t("app.name")}
            </span>
          </motion.div>
        </div>
        <div className="flex items-center space-x-4">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg transition-colors duration-300 dark:bg-gray-800 dark:shadow-gray-900/30"
        >
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <svg
                className="h-12 w-12 text-primary-600 transition-colors dark:text-primary-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 transition-colors dark:text-white">
              {t("auth.enterOtp")}
            </h2>
            <p className="mt-2 text-gray-600 transition-colors dark:text-gray-300">
              {t("auth.otpSentToEmail", { email })}
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-700 transition-colors dark:text-gray-300"
              >
                {t("auth.otp")}
              </label>
              <div className="mt-1">
                <input
                  id="otp"
                  type="text"
                  value={otp.join("")}
                  onChange={handleOtpChange}
                  placeholder="Enter 4-6 digit OTP"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm transition-colors duration-300 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                  maxLength={6}
                  minLength={4}
                />
              </div>
            </div>

            <div className="space-y-4">
              <Button
                onClick={verifyOtp}
                className="w-full"
                variant="primary"
                loading={isLoading}
                disabled={otp.some((digit) => digit === "")}
              >
                {t("auth.verifyOtp")}
              </Button>

              <Button
                onClick={() => navigate("/login", { replace: true })}
                className="w-full"
                variant="outline"
              >
                {t("auth.backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </main>

      <footer className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>
          © {new Date().getFullYear()} Gnzabe. {t("common.allRightsReserved")}
        </p>
      </footer>
    </div>
  );
};

export default OtpVerification;
